import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Radio, X, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 15000;
// Show banner when countdown ≤ this threshold (starts_in_seconds)
const WARN_WINDOW_SECONDS = 60;

/** Floating banner on the landing page — warns the user 1min before their tournament starts.
 * Only visible when logged in AND user has an upcoming/active tournament AND starts_in_seconds <= 60.
 * User can dismiss it, and it stays dismissed per room_id via localStorage.
 */
export default function RoomStartBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const nav = useNavigate();
  const [target, setTarget] = useState(null); // { room_id, label, ends_in_seconds, starts_in_seconds }
  const [nowMs, setNowMs] = useState(Date.now());

  const load = useCallback(async () => {
    if (!user) { setTarget(null); return; }
    try {
      const r = await fetch(`${API}/api/rooms/me/active`, { credentials: "include" });
      if (!r.ok) return;
      const d = await r.json();
      // Prefer the one closest to start (or already running)
      const upcoming = (d.active || [])
        .filter((t) => t.ends_in_seconds > 0)
        .sort((a, b) => a.starts_in_seconds - b.starts_in_seconds)[0];
      setTarget(upcoming || null);
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => { load(); const t = setInterval(load, POLL_MS); return () => clearInterval(t); }, [load]);

  // Local ticking clock (1s) so countdown is smooth without server polls
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!target) return null;

  // Compute live remaining seconds until start (or until end, if already running)
  const startsAtMs = nowMs + target.starts_in_seconds * 1000;
  const endsAtMs = nowMs + target.ends_in_seconds * 1000;
  const secToStart = Math.max(0, Math.round((startsAtMs - Date.now()) / 1000));
  const secToEnd = Math.max(0, Math.round((endsAtMs - Date.now()) / 1000));
  const running = secToStart <= 0 && secToEnd > 0;

  // Only show when starting within 60s OR already running
  if (!running && secToStart > WARN_WINDOW_SECONDS) return null;

  // Was this specific room+phase dismissed?
  const dismissKey = `ufo_room_banner_${target.room_id}_${running ? "run" : "start"}`;
  const dismissed = typeof window !== "undefined" && localStorage.getItem(dismissKey) === "1";
  if (dismissed) return null;

  const label = running ? t("room_start_banner.live") : t("room_start_banner.starts_in");
  const timeText = running
    ? t("room_start_banner.remaining", { time: `${Math.floor(secToEnd / 60)}:${(secToEnd % 60).toString().padStart(2, "0")}` })
    : secToStart <= 0
      ? t("room_start_banner.now")
      : `${secToStart}s`;

  const dismiss = (e) => {
    e.stopPropagation();
    localStorage.setItem(dismissKey, "1");
    setTarget(null);
  };

  return (
    <div
      data-testid="room-start-banner"
      className="fixed bottom-4 right-4 z-[60] w-[calc(100%-2rem)] max-w-sm cursor-pointer group"
      onClick={() => nav("/pilot")}
    >
      <div className="rounded-sm border border-amber-300/70 bg-gradient-to-br from-amber-500/25 to-black/80 backdrop-blur-md shadow-2xl shadow-amber-300/30 p-4 flex items-center gap-3 animate-in fade-in slide-in-from-bottom-4 duration-300">
        <div className={`h-10 w-10 rounded-full border ${running ? "border-emerald-300 bg-emerald-500/20 text-emerald-200" : "border-amber-300 bg-amber-500/20 text-amber-200"} flex items-center justify-center shrink-0`}>
          <Radio className={`h-5 w-5 ${running ? "animate-pulse" : ""}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[9px] tracking-[0.25em] text-secondary/90">{label}</p>
          <p className="font-display font-black text-lg tracking-tighter text-white truncate">{target.label}</p>
          <p className="font-mono text-[10px] tracking-widest text-amber-200 inline-flex items-center gap-1">
            <Clock className="h-3 w-3" /> {timeText} · {t("room_start_banner.tap_to_play")}
          </p>
        </div>
        <button
          onClick={dismiss}
          data-testid="room-start-banner-close"
          className="p-1 rounded-sm text-white/50 hover:text-white hover:bg-white/10 shrink-0"
          aria-label={t("room_start_banner.aria_close")}>
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
